import { FEATURE_FLAGS } from '~/constants';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import {
  ContentsApiResponse,
  mapApiSearchContentResultsFromApiToVm,
} from '~/searchApi';

import { useContentWithMetrics } from '../../hooks/useContentWithMetrics';
import { usePageBuilderStore } from '../../hooks/usePageBuilderStore';
import { AnyBlock } from '../../types';
import {
  mergeContentWithLinks,
  mergeContentWithMetrics,
} from '../../utils/contentUtils';
import { DraggableContent } from './DraggableContent';
import { ReorderGrid } from './ReorderGrid/ReorderGrid';

export interface TalentContentProperties {
  creatorId: string;
  customContent: ContentsApiResponse['results'];
  showInShared: boolean;
}

interface Props extends TalentContentProperties {
  blockId: string;
}

export const TalentContent: React.FC<Props> = ({ blockId }: Props) => {
  const { blocks, updateBlock } = usePageBuilderStore();
  const { isEnabled: isMediaKitsContentMetrics } = useFeature(
    FEATURE_FLAGS.foamMediaKitsContentMetrics,
  );

  // Find the block first
  const block = blocks.find((b) => b.id === blockId) as AnyBlock | undefined;

  // Extract properties safely
  const items = block?.properties
    ? (block.properties as TalentContentProperties).customContent
    : [];
  const showInShared = !isMediaKitsContentMetrics
    ? true
    : block?.properties
      ? (block.properties as TalentContentProperties).showInShared
      : false;

  // Extract content IDs for fetching metrics
  const contentIds = items.map((item) => item.id);

  // Always call the hook, but it will only execute when conditions are met
  const { data: contentWithMetrics } = useContentWithMetrics(
    contentIds,
    showInShared,
    !!block,
  );

  const setItems = (newItems: ContentsApiResponse['results']) => {
    updateBlock(blockId, {
      ...block,
      properties: {
        ...(block?.properties as TalentContentProperties),
        customContent: newItems,
      },
    } as AnyBlock);
  };

  // Merge metrics when visible; otherwise merge only link fields so we can render anchors
  const contentToMap = (() => {
    if (contentWithMetrics && contentWithMetrics.length > 0) {
      return showInShared
        ? mergeContentWithMetrics(items, contentWithMetrics)
        : mergeContentWithLinks(items, contentWithMetrics);
    }
    return items;
  })();

  const mappedContent = mapApiSearchContentResultsFromApiToVm(contentToMap);

  // Use contentToMap directly when showInShared is true (it has all the data we need)
  // Use mappedContent when showInShared is false (original behavior)
  const contentToRender = showInShared ? contentToMap : mappedContent;

  return (
    <>
      <ReorderGrid
        values={items}
        onReorder={setItems}
        className="flex shrink-0 snap-x snap-mandatory gap-4 overflow-x-auto tab:flex-wrap"
      >
        {contentToRender.map((content, index) => (
          <DraggableContent
            key={content.id}
            content={content}
            index={index}
            items={items}
            blockId={blockId}
            isMediaKitsContentMetrics={isMediaKitsContentMetrics}
            showMetrics={showInShared}
          />
        ))}
        <div className="grow-[999]">
          {/* This div is necessary to prevent last image to expand full width */}
        </div>
      </ReorderGrid>
    </>
  );
};
